import React, { useEffect, useState } from 'react';
import { Modal, Form, Select, Button, Upload, Input, Popconfirm } from 'antd';
import { UploadOutlined, QuestionCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment-jalaali';
import 'moment-jalaali';
import { shiftsApi, show422Error, ValidationError } from '../../../utils/api';
import { message } from 'antd';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

const { Option } = Select;
const { TextArea } = Input;

const ShiftUpdateModal = ({ visible, onCancel, onSave, employee, date, shift }) => {
  const { canEdit, canDelete } = useResourcePermissions('shift');
  const [form] = Form.useForm();
  const [shiftsList, setShiftsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [shiftsLoading, setShiftsLoading] = useState(false);
  const [shiftScheduleId, setShiftScheduleId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingShiftData, setLoadingShiftData] = useState(false);

  // تابع تبدیل اعداد فارسی به انگلیسی
  const persianToEnglishDigits = (str) => {
    if (!str) return str;
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
    return str.replace(/[۰-۹]/g, (digit) => englishDigits[persianDigits.indexOf(digit)]);
  };

  // تابع برای فرمت تاریخ جلالی با اعداد انگلیسی
  const formatJalaliDate = (date) => {
    if (!date) return null;
    
    // اگر رشته است، اعداد فارسی را به انگلیسی تبدیل کن
    if (typeof date === 'string') {
      return persianToEnglishDigits(date);
    }
    
    // اگر moment object است
    if (date.jYear && typeof date.jYear === 'function') {
      const jYear = date.jYear();
      const jMonth = String(date.jMonth() + 1).padStart(2, '0');
      const jDay = String(date.jDate()).padStart(2, '0');
      return `${jYear}/${jMonth}/${jDay}`;
    }
    
    // اگر DateObject از react-multi-date-picker است
    if (date.format && typeof date.format === 'function') {
      const formatted = date.format('YYYY/MM/DD');
      return persianToEnglishDigits(formatted);
    }
    
    // اگر DateObject با jYear, jMonth, jDay است
    if (date.jYear !== undefined && date.jMonth !== undefined && date.jDay !== undefined) {
      const jYear = date.jYear;
      const jMonth = String(date.jMonth + 1).padStart(2, '0');
      const jDay = String(date.jDay).padStart(2, '0');
      return `${jYear}/${jMonth}/${jDay}`;
    }
    
    return null;
  };

  // دریافت اطلاعات شیفت برای ویرایش
  const loadShiftData = async () => {
    if (!employee || !date || !shift || shift.type === 'empty') {
      setIsEditMode(false);
      setShiftScheduleId(null);
      return;
    }

    setLoadingShiftData(true);
    try {
      const jDate = moment(date);
      const day = String(jDate.jDate()).padStart(2, '0');
      const month = String(jDate.jMonth() + 1).padStart(2, '0');
      const year = jDate.jYear();

      const res = await shiftsApi.getMark({
        userid: employee.id,
        day,
        month,
        year,
      });

      if (res.data && res.data.shift_schedule) {
        const shiftSchedule = res.data.shift_schedule;
        setShiftScheduleId(shiftSchedule.id);
        setIsEditMode(true);
        
        form.setFieldsValue({
          user_id: employee.id,
          shift_date: formatJalaliDate(jDate),
          employee_shift_id: shiftSchedule.employee_shift_id,
          remarks: shiftSchedule.remarks || '',
        });
      } else {
        setIsEditMode(false);
        setShiftScheduleId(null);
      }
    } catch (error) {
      console.error('خطا در دریافت اطلاعات شیفت:', error);
      setIsEditMode(false);
      setShiftScheduleId(null);
    } finally {
      setLoadingShiftData(false);
    }
  };

  useEffect(() => {
    if (visible) {
      loadShiftsList();
      if (employee && date) {
        const jDate = moment(date);
        // اگر shift موجود است، اطلاعات را از API بگیر
        if (shift && shift.type !== 'empty') {
          loadShiftData();
        } else {
          // حالت ایجاد جدید
          setIsEditMode(false);
          setShiftScheduleId(null);
          form.setFieldsValue({
            user_id: employee.id,
            shift_date: formatJalaliDate(jDate),
            employee_shift_id: null,
            remarks: '',
          });
        }
      }
    } else {
      form.resetFields();
      setIsEditMode(false);
      setShiftScheduleId(null);
    }
  }, [visible, employee, date, shift]);

  const loadShiftsList = async () => {
    setShiftsLoading(true);
    try {
      const res = await shiftsApi.getList();
      const shifts = res.data?.shifts || [];
      setShiftsList(shifts);
    } catch (error) {
      console.error('خطا در دریافت لیست شیفت‌ها:', error);
      message.error('خطا در دریافت لیست شیفت‌ها');
    } finally {
      setShiftsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // تبدیل shift_date به فرمت مورد نیاز با اعداد انگلیسی
      let shiftDate = values.shift_date;
      if (typeof shiftDate === 'string') {
        shiftDate = persianToEnglishDigits(shiftDate);
      } else if (shiftDate && shiftDate.format) {
        shiftDate = formatJalaliDate(shiftDate);
      } else if (shiftDate && shiftDate.jYear) {
        shiftDate = formatJalaliDate(shiftDate);
      }
      
      const payload = {
        user_id: values.user_id,
        employee_shift_id: values.employee_shift_id,
        shift_date: shiftDate,
        remarks: values.remarks || '',
      };

      if (isEditMode && shiftScheduleId) {
        // ویرایش
        await shiftsApi.update(shiftScheduleId, payload);
        message.success('شیفت با موفقیت ویرایش شد');
      } else {
        // ایجاد جدید
        await shiftsApi.store(payload);
        message.success('شیفت با موفقیت ثبت شد');
      }
      
      if (onSave) {
        onSave({
          ...values,
          date: moment(date).format('YYYY-MM-DD'),
        });
      }
      
      form.resetFields();
      setIsEditMode(false);
      setShiftScheduleId(null);
      onCancel();
    } catch (error) {
      console.error('خطا در ذخیره شیفت:', error);
      
      // اگر خطای validation است، به اولین فیلد خطادار اسکرول کن
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      }
      
      // اگر خطای API است (422)
      if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, form);
      } else if (!error.errorFields) {
        // فقط اگر خطای validation نبود، پیام خطا نمایش بده
        message.error(error?.message || 'خطا در ذخیره شیفت');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!shiftScheduleId) return;

    setDeleteLoading(true);
    try {
      await shiftsApi.delete(shiftScheduleId);
      message.success('شیفت با موفقیت حذف شد');
      
      if (onSave) {
        onSave({
          date: moment(date).format('YYYY-MM-DD'),
        });
      }
      
      form.resetFields();
      setIsEditMode(false);
      setShiftScheduleId(null);
      onCancel();
    } catch (error) {
      console.error('خطا در حذف شیفت:', error);
      message.error(error?.message || 'خطا در حذف شیفت');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const jDate = moment(date);
    const dayName = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه'][jDate.day()];
    const day = jDate.jDate();
    const month = jDate.jMonth() + 1;
    const year = jDate.jYear();
    return `${year}/${month}/${day} (${dayName})`;
  };

  const formatShiftTime = (shift) => {
    if (!shift) return '';
    const start = shift.office_start_time || '00:00:00';
    const end = shift.office_end_time || '00:00:00';
    return `${start.substring(0, 5)} - ${end.substring(0, 5)}`;
  };

  const uploadProps = {
    beforeUpload: () => false,
    maxCount: 1,
  };

  return (
    <Modal
      title={
        <span style={{ fontFamily: 'CustomFont' }}>
          {isEditMode ? 'ویرایش شیفت' : 'اختصاص شیفت'}
        </span>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      style={{ fontFamily: 'CustomFont' }}
    >
      {loadingShiftData ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500" style={{ fontFamily: 'CustomFont' }}>
            در حال بارگذاری...
          </div>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ fontFamily: 'CustomFont' }}
        >
          <div className="mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2" style={{ fontFamily: 'CustomFont' }}>
              تاریخ: {formatDate(date)}
            </div>
            {employee && (
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold"
                >
                  {employee.name?.charAt(0) || '?'}
                </div>
                <div>
                  <div className="font-semibold text-gray-800 dark:text-gray-200" style={{ fontFamily: 'CustomFont' }}>
                    {employee.name}
                  </div>
                  {employee.designation_name && (
                    <div className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
                      {employee.designation_name}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>کارمند</span>}
            name="user_id"
            rules={[{ required: true, message: 'لطفاً کارمند را انتخاب کنید' }]}
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ</span>}
            name="shift_date"
            rules={[{ required: true, message: 'لطفاً تاریخ را وارد کنید' }]}
            hidden
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>شیفت</span>}
            name="employee_shift_id"
            rules={[{ required: true, message: 'لطفاً شیفت را انتخاب کنید' }]}
          >
            <Select
              placeholder="انتخاب شیفت"
              style={{ fontFamily: 'CustomFont' }}
              loading={shiftsLoading}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {shiftsList.map((shiftItem) => (
                <Option key={shiftItem.id} value={shiftItem.id}>
                  <div className="flex items-center gap-2">
                    {shiftItem.color && (
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: shiftItem.color }}
                      />
                    )}
                    <span>
                      {shiftItem.shift_name} ({shiftItem.shift_short_code})
                      {shiftItem.office_start_time && shiftItem.office_end_time && (
                        <span className="text-gray-500 text-xs mr-2">
                          - {formatShiftTime(shiftItem)}
                        </span>
                      )}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>یادداشت</span>}
            name="remarks"
          >
            <TextArea
              rows={3}
              placeholder="یادداشت اختیاری..."
              style={{ fontFamily: 'CustomFont' }}
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                افزودن فایل{' '}
                <QuestionCircleOutlined className="text-gray-400" />
              </span>
            }
            name="file"
          >
            <Upload.Dragger
              {...uploadProps}
              className="custom-upload"
              style={{ fontFamily: 'CustomFont' }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text" style={{ fontFamily: 'CustomFont' }}>
                یک فایل انتخاب کنید
              </p>
            </Upload.Dragger>
          </Form.Item>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              {isEditMode && shiftScheduleId && canDelete ? (
                <Popconfirm
                  title="آیا مطمئن هستید که می‌خواهید این شیفت را حذف کنید؟"
                  onConfirm={handleDelete}
                  okText="بله"
                  cancelText="خیر"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    size="middle"
                    loading={deleteLoading}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    حذف
                  </Button>
                </Popconfirm>
              ) : null}
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={onCancel}
                size="middle"
                style={{ fontFamily: 'CustomFont' }}
                disabled={loading || deleteLoading}
              >
                بستن
              </Button>
              {canEdit ? (
                <Button
                  type="primary"
                  htmlType="submit"
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-green-600"
                  style={{ fontFamily: 'CustomFont' }}
                  loading={loading}
                  disabled={deleteLoading}
                >
                  {isEditMode ? 'ویرایش' : 'ذخیره'}
                </Button>
              ) : null}
            </div>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default ShiftUpdateModal;
